import { ROLL_COST_FIRST, ROLL_COST_STEP, ROLL_DISCOUNT } from '../constants';
import { boardEnergy, energyCapacity } from '../engine/roles';
import type { Board, EnergyGain, Move, Piece, Rules, Side } from '../engine/types';
import type { SpellId } from '../spells/types';

export type Phase = 'roll' | 'aim' | 'move' | 'over';

/**
 * Who picks the moves. In `classic` each side picks its own. In `gods` the
 * search picks the moves of both sides, and a side only rolls and aims.
 * Rob named the second one Godsmode, 2026-10-12: "the same chess engine will
 * plan moves for both sides. But the player and AI will still be deploying
 * spells into that match".
 */
export type MatchMode = 'classic' | 'gods';
export const DEFAULT_MATCH_MODE: MatchMode = 'classic';
export const isMatchMode = (value: unknown): value is MatchMode =>
  value === 'classic' || value === 'gods';

/**
 * The type of thing a log line records. A spell writes in its polarity. The
 * rest name what the rules did: a move, a capture, a roll of the die, a change
 * of a wheel face, a timer that ran out or fired, or a note about the match.
 */
export type LogTone =
  | 'ally'
  | 'enemy'
  | 'any'
  | 'board'
  | 'move'
  | 'capture'
  | 'roll'
  | 'wheel'
  | 'timer'
  | 'system';

export type LogEntry = {
  id: number;
  ply: number;
  tone: LogTone;
  text: string;
  /** The spell that wrote this line, when one did. */
  spell?: string;
  spellId?: SpellId;
};

export type RollRecord = {
  serial: number;
  face: number;
  /**
   * The spell of the face. In a match with casters the die gives energy, and
   * this is the scroll the roll gave, or null.
   */
  spellId: SpellId | null;
  /** The energy the roll gave, in a match with casters. Null in a match with a wheel. */
  gain: number | null;
  /** The id of the first line of the log the roll wrote. A view holds those lines until the die lands. */
  firstLogId: number;
  caster: Side;
  /** The ply it was rolled on. The hand holds the card for that ply. */
  ply: number;
};

/** One piece that a spell took from one square to another. */
export type PieceMove = {
  pieceId: number;
  from: number;
  to: number;
};

/**
 * One part of a spell that the narrator shows on its own: the pieces that
 * move at the same moment, and the line that says so.
 */
export type CastStep = {
  moves: readonly PieceMove[];
  text: string;
};

export type CastRecord = {
  /** Matches the roll that caused it. */
  serial: number;
  spellId: SpellId;
  caster: Side;
  /** The square the caster aimed at, when the spell was aimed. */
  source: number | null;
  /** Squares the spell changed. */
  squares: readonly number[];
  /** The first line the spell wrote to the log. */
  line: string;
  /** The spell had nothing to do. The caster gets one free roll a turn. */
  hadNoEffect: boolean;
  /** The moves the narrator shows one step after the other. Empty for most spells. */
  steps: readonly CastStep[];
  /** The serial of the first event after the cast began. The narrator plays the cast before it. */
  firstEvent: number;
};

/**
 * Something the rules did: a blast, a wake, a random move, a captor removed, a promotion.
 * The narrator plays each one as a beat. Squares get the changed mark. Aside
 * squares get the dashed mark: where a randomNextMove piece meant to go.
 */
export type EventRecord = {
  serial: number;
  /** Short, for the cue bar: "A dormant piece wakes". */
  caption: string;
  text: string;
  squares: readonly number[];
  aside: readonly number[];
  tone: LogTone;
  /** Energy the event gave a side, from its first square. */
  energy?: EnergyGain;
};

export type GameState = {
  seed: number;
  mode: MatchMode;
  board: Board;
  rules: Rules;
  turn: Side;
  /** Half moves completed. Turn number for a side is floor(ply / 2) + 1. */
  ply: number;
  phase: Phase;
  wheel: readonly SpellId[];
  /** A face that was rolled this turn. The wheel looks at it when the turn ends. */
  wheelFaceToRefresh: number | null;
  lastRoll: RollRecord | null;
  lastCast: CastRecord | null;
  /** Recent events, oldest first. The narrator plays the ones it has not seen. */
  events: readonly EventRecord[];
  nextEventSerial: number;
  pendingSpell: SpellId | null;
  pendingTargets: readonly number[];
  /** Rolls the side is owed at no cost: after a spell with no effect, or from a spell. */
  pendingRolls: number;
  /**
   * The energy of each side in a match with a wheel. In Godsmode the pieces
   * hold the energy, and this stays at zero. Read `energyOf`, not this.
   */
  energy: Readonly<Record<Side, number>>;
  /** The rolls each side paid for. The cost of the next one rises with the count. */
  paidRolls: Readonly<Record<Side, number>>;
  /** Turns of its own on which the side gains no energy. */
  energyHold: Readonly<Record<Side, number>>;
  /**
   * The casters of each side, by id, in the order of their slots. A side has
   * the skills and the scroll slots of all of them. Null in a match with a
   * wheel. See content/casters.ts.
   */
  casters: Readonly<Record<Side, readonly string[]>> | null;
  /** The scrolls each side holds, in the order of its scroll slots. */
  scrolls: Readonly<Record<Side, readonly SpellId[]>>;
  /** The army of the side fell to half strength at some point. Its skills for that stay unlocked. */
  halfStrength: Readonly<Record<Side, boolean>>;
  /** The strength of one army at the start. */
  startStrength: number;
  /** The skills each side paid for. The AI reads it to take its skills in turn. */
  skillCasts: Readonly<Record<Side, number>>;
  /** Every cast of the match. The serial of a cast from a slot comes from it. */
  castCount: number;
  /** The slot the spell in the air came from. The cast pays for it when it lands. */
  pendingSlot: number | null;
  /** Rolls of the side that cost less. */
  rollDiscounts: Readonly<Record<Side, number>>;
  /** Set by a spell's noEffect helper. The flow reads and clears it. */
  hadNoEffect: boolean;
  /** Set by a spell that moves pieces in steps. The flow puts it on the cast record and clears it. */
  castSteps: readonly CastStep[];
  /** The turns in a row that ended with no move. A move or a cast starts the count again. */
  passes: number;
  /** The plies in a row that ended with as many pieces on the board as the ply before. */
  quietPlies: number;
  /** The pieces of both sides on the board at the end of the last ply. */
  piecesAtPlyEnd: number;
  /** One free roll after a noEffect, each turn. */
  freeRollUsed: boolean;
  /** Set by Double Time. The side must move this piece again. */
  mustMovePieceId: number | null;
  /** The side that gets a suggestion for its move this turn. The turn end clears it. */
  suggestFor: Side | null;
  /** The spell that makes the piece move again, when one does. The cue names it. */
  moveAgainSpellId: SpellId | null;
  lastMove: Move | null;
  winner: Side | null;
  /** What ended the match, for the final card. */
  endLine: string;
  log: readonly LogEntry[];
  captured: readonly Piece[];
  /** Boards at the start of recent plies, oldest first. Time Loop reads it. */
  boardHistory: readonly Board[];
  nextPieceId: number;
  nextLogId: number;
};

export const sideTurnNumber = (state: GameState): number => Math.floor(state.ply / 2) + 1;

/**
 * The energy of the side. In Godsmode it is what its pieces hold: the rooks,
 * at the start. Rob, 2026-10-13: "The player's energy reserves are now
 * represented by the rooks". In a match with a wheel it is a count.
 */
export const energyOf = (state: GameState, side: Side): number =>
  state.mode === 'gods' ? boardEnergy(state.board, side) : state.energy[side];

/** The most energy the side can hold. A match with a wheel has no limit. */
export const energyCapacityOf = (state: GameState, side: Side): number =>
  state.mode === 'gods' ? energyCapacity(state.board, side) : Number.POSITIVE_INFINITY;

/** What the next roll costs the side. */
export const rollCost = (state: GameState, side: Side): number => {
  const full = ROLL_COST_FIRST + state.paidRolls[side] * ROLL_COST_STEP;
  return state.rollDiscounts[side] > 0 ? Math.max(0, full - ROLL_DISCOUNT) : full;
};

/**
 * The side to move may pay for a roll now: before its move, with the energy
 * for it. Never in Godsmode, where the one roll of a turn gives the energy.
 */
export const canPayForRoll = (state: GameState): boolean =>
  state.mode !== 'gods' &&
  state.phase === 'move' &&
  state.mustMovePieceId === null &&
  energyOf(state, state.turn) >= rollCost(state, state.turn);

/** A roll may start now: one the side is owed, or one it can pay for. */
export const canRoll = (state: GameState): boolean =>
  state.phase === 'roll' || canPayForRoll(state);
